import axios from 'axios';

// API Configuration
const getApiUrl = () => {
  // Determine environment based on hostname or env variable
  const isLocal = window.location.hostname === 'localhost' || 
                  window.location.hostname === '127.0.0.1' || 
                  process.env.NODE_ENV === 'development';
  
  // In development, use proxy (relative URLs)
  // In production, use environment variable or fallback to existing Streamlit app API
  return isLocal 
    ? '' // Empty string uses proxy from package.json
    : process.env.REACT_APP_API_URL || 'https://app.lotwizard.us/api';
};

const API_URL = getApiUrl();

// Create axios instance
const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 30000, // 30 second timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// Log the configuration for debugging
console.log('API Configuration:', {
  API_URL,
  baseURL: apiClient.defaults.baseURL,
  isDevelopment: process.env.NODE_ENV === 'development',
  hostname: window.location.hostname
});

// Request interceptor to add auth headers (optional - matches Streamlit behavior)
apiClient.interceptors.request.use(
  (config) => {
    // The Streamlit version doesn't always use auth headers, making this optional
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    console.log('Request config:', {
      url: config.url,
      method: config.method,
      params: config.params,
      headers: config.headers
    });
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized - clear token and redirect to login
      localStorage.removeItem('access_token');
      // You could redirect to login page here
    }
    return Promise.reject(error);
  }
);

/**
 * Search loads API function - mirrors the Streamlit api_search_to_df function
 * @param {Object} searchFilters - Search parameters
 * @returns {Promise<Array>} - Array of load data
 */
export const searchLoads = async (searchFilters) => {
  try {
    console.log('=== API SEARCH DEBUG ===');
    console.log('API_URL:', API_URL);
    console.log('Base URL:', apiClient.defaults.baseURL);
    console.log('Request URL:', '/search-loads/');
    console.log('Full resolved URL:', apiClient.defaults.baseURL + '/search-loads/');
    console.log('Search Parameters:', searchFilters);
    console.log('Headers:', apiClient.defaults.headers);
    
    const response = await apiClient.get('/search-loads/', {
      params: searchFilters
    });

    console.log('API Response Status:', response.status);
    console.log('API Response Data:', response.data);

    if (response.status === 200) {
      let data = response.data;
      
      // Handle different response formats
      if (Array.isArray(data)) {
        // Direct array of results
        return data;
      } else if (data && typeof data === 'object' && 'results' in data) {
        // Paginated results
        return data.results;
      } else if (data && typeof data === 'object') {
        // Single object response, wrap in array
        return [data];
      } else {
        // Empty or invalid response
        return [];
      }
    } else {
      throw new Error(`API returned status ${response.status}`);
    }
  } catch (error) {
    console.error('API Error:', error);
    
    if (error.response) {
      // Server responded with error status
      const status = error.response.status;
      const message = error.response.data?.message || error.response.data?.detail || error.response.statusText;
      
      if (status === 400) {
        throw new Error(`Invalid search parameters: ${message}`);
      } else if (status === 401) {
        throw new Error('Authentication required. Please log in.');
      } else if (status === 403) {
        throw new Error('Access denied. Please check your permissions.');
      } else if (status === 404) {
        throw new Error('Search endpoint not found.');
      } else if (status >= 500) {
        throw new Error('Server error. Please try again later.');
      } else {
        throw new Error(`API Error (${status}): ${message}`);
      }
    } else if (error.request) {
      // Network error
      throw new Error('Network error. Please check your connection and try again.');
    } else {
      // Other error
      throw new Error(`Search failed: ${error.message}`);
    }
  }
};

/**
 * Multi-city search API function - mirrors the Streamlit api_multi_city_to_df function
 * @param {Object} searchFilters - Search parameters for multiple cities
 * @returns {Promise<Array>} - Array of load data
 */
export const searchMultiCity = async (searchFilters) => {
  try {
    const response = await apiClient.get('/search-multi-trip/', {
      params: searchFilters
    });

    if (response.status === 200) {
      let data = response.data;
      
      if (Array.isArray(data)) {
        return data;
      } else if (data && typeof data === 'object' && 'results' in data) {
        return data.results;
      } else {
        return [];
      }
    } else {
      throw new Error(`API returned status ${response.status}`);
    }
  } catch (error) {
    console.error('Multi-city search error:', error);
    throw error;
  }
};

/**
 * Update user defaults - mirrors the Streamlit update_defaults function
 * @param {Object} defaults - User default settings
 * @returns {Promise<Object>} - Response data
 */
export const updateUserDefaults = async (defaults) => {
  try {
    const response = await apiClient.put('/user/defaults/', defaults);
    return response.data;
  } catch (error) {
    console.error('Update defaults error:', error);
    throw error;
  }
};

/**
 * Add search log - mirrors the Streamlit add_search_log function
 * @param {Object} logData - Search log data
 * @returns {Promise<Object>} - Response data
 */
export const addSearchLog = async (logData) => {
  try {
    const response = await apiClient.post('/user/search-log/', logData);
    return response.data;
  } catch (error) {
    console.error('Add search log error:', error);
    throw error;
  }
};

/**
 * Save email alert - mirrors the Streamlit save_email_alert function
 * @param {Object} alertData - Email alert data
 * @returns {Promise<Object>} - Response data
 */
export const saveEmailAlert = async (alertData) => {
  try {
    const response = await apiClient.post('/user/email-alert/', alertData);
    return response.data;
  } catch (error) {
    console.error('Save email alert error:', error);
    throw error;
  }
};

/**
 * Get or create user - mirrors the Streamlit get_or_create_user function
 * @param {string} userId - User ID from Auth0
 * @param {string} userName - User name
 * @param {string} userEmail - User email
 * @returns {Promise<Object>} - User data including role and settings
 */
export const getOrCreateUser = async (userId, userName, userEmail) => {
  try {
    const params = {
      user_id: userId,
      user_name: userName,
      user_email: userEmail
    };
    
    const response = await apiClient.get('/getuser/', { params });
    return response.data;
  } catch (error) {
    console.error('Get or create user error:', error);
    throw error;
  }
};

/**
 * Get user profile data
 * @returns {Promise<Object>} - User profile data
 */
export const getUserProfile = async () => {
  try {
    const response = await apiClient.get('/user/profile/');
    return response.data;
  } catch (error) {
    console.error('Get user profile error:', error);
    throw error;
  }
};

/**
 * Update user role (Admin only)
 * @param {number} userId - User ID to update
 * @param {string} newRole - New role to assign
 * @returns {Promise<Object>} - Response data
 */
export const updateUserRole = async (userId, newRole) => {
  try {
    const payload = {
      user_id_int: userId,
      user_role: newRole
    };
    
    const response = await apiClient.put('/update_user_role', payload);
    return response.data;
  } catch (error) {
    console.error('Update user role error:', error);
    throw error;
  }
};

/**
 * Get user list (Admin only)
 * @param {Array} testAccounts - Array of test account IDs to exclude
 * @returns {Promise<Array>} - List of users
 */
export const getUserList = async (testAccounts = [0]) => {
  try {
    const params = {
      test_accounts: testAccounts
    };
    
    const response = await apiClient.get('/users_list', { params });
    return response.data;
  } catch (error) {
    console.error('Get user list error:', error);
    throw error;
  }
};

/**
 * Test API connectivity using the search endpoint
 * @returns {Promise<boolean>} - True if API is reachable
 */
export const testApiConnectivity = async () => {
  try {
    console.log('Testing API connectivity to:', API_URL);
    const response = await apiClient.get('/search-loads/', { 
      timeout: 5000,
      params: { test: 'connectivity' } // minimal params to test connection
    });
    console.log('API connectivity test passed:', response.status);
    return true;
  } catch (error) {
    console.warn('API connectivity test failed:', error.message);
    return false;
  }
};

/**
 * Authentication functions
 */
export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem('access_token', token);
  } else {
    localStorage.removeItem('access_token');
  }
};

export const getAuthToken = () => {
  return localStorage.getItem('access_token');
};

export const clearAuthToken = () => {
  localStorage.removeItem('access_token');
};

export default apiClient;